import React from 'react';
import { CalendarClock } from 'lucide-react';
import Tooltip from '@components/common/Tooltip';
import { formatVersionDate, futureVersionChanges } from '@utils/divisionVersionUtils';
import type { DivisionVersion } from '@app-types/division';
import styles from './DivisionFutureVersionIndicator.module.css';

export interface DivisionFutureVersionIndicatorProps {
  current: DivisionVersion;
  future: DivisionVersion;
  /** Test id of the icon; the tooltip gets `${dataTestId}-tooltip`. */
  dataTestId?: string;
}

const NO_CHANGES_TEXT = 'No changes to name, description or leads';

/**
 * Calendar icon next to a Division Name whose tooltip lists what the Future Version
 * changes and from when, so the row itself keeps showing the Current Version (ADO-1159).
 */
const DivisionFutureVersionIndicator: React.FC<DivisionFutureVersionIndicatorProps> = ({
  current,
  future,
  dataTestId = 'division-future-version'
}) => {
  const heading = `Version ${future.version} from ${formatVersionDate(future.effective_date)}`;
  const changes = futureVersionChanges(current, future);
  // The portalled tooltip is not linked to the icon, so the label carries its full text for screen readers.
  const summary =
    changes.length === 0 ? NO_CHANGES_TEXT : changes.map((change) => `${change.label}: ${change.value}`).join('; ');

  const content = (
    <div className={styles.content}>
      <div className={styles.heading} data-test-id={`${dataTestId}-heading`}>
        {heading}
      </div>
      {changes.length === 0 ? (
        <div>{NO_CHANGES_TEXT}</div>
      ) : (
        changes.map((change) => (
          <div key={change.label} data-test-id={`${dataTestId}-change`}>
            <span className={styles.label}>{change.label}:</span> {change.value}
          </div>
        ))
      )}
    </div>
  );

  // The tab stop sits on the icon itself (not Tooltip's `focusable` wrapper) so focus lands on the element that has the accessible name.
  return (
    <Tooltip content={content} position="top" width="auto" dataTestId={dataTestId}>
      <span
        className={styles.icon}
        tabIndex={0}
        role="img"
        aria-label={`Future version ${heading}. ${summary}`}
        data-test-id={dataTestId}
      >
        <CalendarClock size={14} aria-hidden />
      </span>
    </Tooltip>
  );
};

export default DivisionFutureVersionIndicator;
